import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Route, Switch, Redirect, withRouter } from 'react-router-dom';
import './index.css';
import './tailwind-output.css';
import Homepage from './components/Homepage';
import Post from './components/Post';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Search from './components/Search';
import ProductViewRedesign from './components/ProductViewRedesign';
import ShareRedirect from './components/ShareRedirect';
import PurchasePost from './components/PurchasePost';
import PurchaseSharedPost from './components/PurchaseSharedPost';
// Notifications import removed - notifications now in navbar dropdown
import Discover from './components/Discover';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import { unregister as unregisterServiceWorker } from './registerServiceWorker';
import ReviewsList from './components/ReviewsList';
// FollowerList and FollowingList removed — followers/following now shown as modal in Profile.js
import PostSharesList from './components/PostSharesList';
import PostFavoritesList from './components/PostFavoritesList';
import DeepDescription from './components/DeepDescription';
import EditPost from './components/EditPost';
import BulkPost from './components/BulkPost';
import SharingDescription from './components/SharingDescription';
import CardDetail from './components/CardDetail';
import Browse from './components/Browse';
import AllListings from './components/AllListings';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { CartProvider } from './contexts/CartContext';
import { GameProvider } from './contexts/GameContext';
import AuthModal from './components/AuthModal';
import AuthRedirect from './components/AuthRedirect';
import OrdersPage from './components/OrdersPage';
import CartPage from './components/CartPage';
import CartCheckout from './components/CartCheckout';
import OrderConfirmation from './components/OrderConfirmation';
import ShippingGuide from './components/ShippingGuide';
import CartOptimizer from './components/CartOptimizer';
import ContactUs from './components/ContactUs';
import HowToSell from './components/HowToSell';
import HowToBuy from './components/HowToBuy';
import ReceivingPayouts from './components/ReceivingPayouts';
import FAQ from './components/FAQ';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import AdminLayout from './components/admin/AdminLayout';
import Messages from './components/Messages';
// BUYLIST FEATURE: disabled — preserved for future use
// import BuylistManager from './components/BuylistManager';
// import BuylistCheckout from './components/BuylistCheckout';
import AccountStanding from './components/AccountStanding';
import Layout from './components/Layout';
import Toast from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';
import BetaGate from './components/BetaGate';
import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import Config from './config';

Amplify.configure({
  Auth: {
    region: Config.COGNITO.REGION,
    userPoolId: Config.COGNITO.USER_POOL_ID,
    userPoolWebClientId: Config.COGNITO.USER_POOL_WEB_CLIENT_ID,
    identityPoolId: Config.COGNITO.IDENTITY_POOL_ID,
  },
  oauth: {},
});

Sentry.init({
  dsn: Config.SENTRY_DSN,
  sendDefaultPii: true,
  environment: process.env.NODE_ENV,
});

class ScrollToTopComponent extends React.Component {
  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      window.scrollTo(0, 0);
    }
  }
  render() { return this.props.children || null; }
}
var ScrollToTop = withRouter(ScrollToTopComponent);

// Detects new deploys by polling the build manifest and silently reloads on
// the user's next route change, so long-lived tabs pick up new bundles
// without interrupting whatever they're doing mid-page.
var UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

class UpdateOnNavigateComponent extends React.Component {
  constructor(props) {
    super(props);
    this.baselineMainJs = null;
    this.newBuildAvailable = false;
    this.checkForUpdate = this.checkForUpdate.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  fetchMainJs() {
    return fetch('/asset-manifest.json', { cache: 'no-store' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (manifest) {
        return manifest && manifest.files ? manifest.files['main.js'] : null;
      })
      .catch(function () { return null; });
  }

  checkForUpdate() {
    var self = this;
    this.fetchMainJs().then(function (mainJs) {
      if (!mainJs) return;
      if (!self.baselineMainJs) {
        self.baselineMainJs = mainJs;
      } else if (mainJs !== self.baselineMainJs) {
        self.newBuildAvailable = true;
      }
    });
  }

  onVisibilityChange() {
    if (document.visibilityState === 'visible') this.checkForUpdate();
  }

  componentDidMount() {
    this.checkForUpdate();
    this.intervalId = setInterval(this.checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  componentWillUnmount() {
    clearInterval(this.intervalId);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  componentDidUpdate(prevProps) {
    if (this.newBuildAvailable && this.props.location.pathname !== prevProps.location.pathname) {
      window.location.reload();
    }
  }

  render() { return null; }
}
var UpdateOnNavigate = withRouter(UpdateOnNavigateComponent);

class MaintenanceGate extends React.Component {
  constructor(props) {
    super(props);
    this.state = { checking: true, maintenance: null, isAdmin: false, showLogin: false, loginView: false, loginEmail: '', loginPassword: '', loginError: '', loginLoading: false, loginSuccess: false, fadingOut: false };
    this.checkStatus = this.checkStatus.bind(this);
    this.handleAuthChanged = this.handleAuthChanged.bind(this);
    this.handleLogin = this.handleLogin.bind(this);
    this.openLogin = this.openLogin.bind(this);
    this.closeLogin = this.closeLogin.bind(this);
  }

  componentDidMount() {
    // Re-check when user logs in (admin may have just authenticated)
    window.addEventListener('authStateChanged', this.handleAuthChanged);
    this.checkAdminAndStatus();
  }

  componentWillUnmount() {
    window.removeEventListener('authStateChanged', this.handleAuthChanged);
  }

  handleAuthChanged() {
    // After login/logout, re-check admin status
    this.checkAdminAndStatus();
  }

  checkAdminAndStatus() {
    var _this = this;
    try {
      var profileStr = localStorage.getItem('primal_user_profile');
      if (profileStr) {
        var profile = JSON.parse(profileStr);
        if (profile.role === 'admin') {
          // If we were showing maintenance (login just happened), animate out
          if (_this.state.maintenance) {
            _this.setState({ loginSuccess: true, loginLoading: false });
            setTimeout(function () {
              _this.setState({ fadingOut: true });
            }, 800);
            setTimeout(function () {
              _this.setState({ checking: false, isAdmin: true, maintenance: null, fadingOut: false, loginSuccess: false });
            }, 1400);
          } else {
            _this.setState({ checking: false, isAdmin: true, maintenance: null });
          }
          return;
        }
      }
    } catch (e) { /* not logged in or bad token */ }
    this.setState({ isAdmin: false });
    this.checkStatus();
  }

  checkStatus() {
    var _this = this;
    _this.setState({ checking: true });
    fetch(Config.DOMAIN + '/api/v1/maintenance-status', { mode: 'cors', method: 'GET' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.enabled) {
          _this.setState({ checking: false, maintenance: data });
        } else {
          _this.setState({ checking: false, maintenance: null });
        }
      })
      .catch(function () { _this.setState({ checking: false, maintenance: null }); });
  }

  handleLogin(e) {
    e.preventDefault();
    var _this = this;
    var email = this.state.loginEmail.trim();
    var password = this.state.loginPassword;
    if (!email || !password) {
      this.setState({ loginError: 'Email and password are required' });
      return;
    }
    this.setState({ loginLoading: true, loginError: '' });
    var AuthService = require('./components/AuthService').default;
    var authSvc = new AuthService();
    authSvc.login(email, password)
      .then(function () {
        window.dispatchEvent(new Event('authStateChanged'));
      })
      .catch(function (err) {
        _this.setState({ loginLoading: false, loginError: err.message || 'Invalid credentials' });
      });
  }

  openLogin() {
    var _this = this;
    // Phase 1: fade out maintenance content
    _this.setState({ showLogin: 'exiting' });
    // Phase 2: after exit animation, show login form
    setTimeout(function () {
      _this.setState({ showLogin: true, loginView: true });
    }, 300);
  }

  closeLogin() {
    var _this = this;
    // Phase 1: fade out login form
    _this.setState({ loginView: false });
    // Phase 2: after exit animation, show maintenance content
    setTimeout(function () {
      _this.setState({ showLogin: false, loginEmail: '', loginPassword: '', loginError: '' });
    }, 300);
  }

  render() {
    if (this.state.checking) return null;
    if (this.state.isAdmin && !this.state.fadingOut) return this.props.children;
    if (!this.state.maintenance && !this.state.fadingOut && !this.state.loginSuccess) return this.props.children;

    var msg = this.state.maintenance ? (this.state.maintenance.message || "We're performing scheduled maintenance. Please check back shortly.") : '';
    var _this = this;
    var fadingOut = this.state.fadingOut;
    var loginSuccess = this.state.loginSuccess;

    return (
      <div>
        {/* Render children underneath during fade-out so site is visible */}
        {fadingOut && this.props.children}

        <div style={{
          position: 'fixed', inset: 0, zIndex: 99998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#0a0a0f', fontFamily: "'Lato', sans-serif",
          overflow: 'hidden',
          animation: fadingOut ? 'maintFadeOut 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
        }}>
        {/* Dot pattern */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: 'radial-gradient(circle at 25% 25%, #c9a84c 1px, transparent 1px), radial-gradient(circle at 75% 75%, #c9a84c 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }} />

        {/* Ambient gold glow behind card */}
        <div style={{
          position: 'absolute', width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)',
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          animation: 'maintGlow 4s ease-in-out infinite alternate',
        }} />

        {/* Card */}
        <div style={{
          position: 'relative', width: '100%', maxWidth: 440, margin: '0 20px', padding: '48px 36px',
          background: 'linear-gradient(145deg, #1a1a2e 0%, #16162a 100%)', borderRadius: 24,
          boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 60px rgba(201,168,76,0.06), inset 0 1px 0 rgba(255,255,255,0.04)',
          border: '1px solid rgba(201,168,76,0.12)', textAlign: 'center',
          animation: 'maintCardIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards',
        }}>

          {loginSuccess ? (
            <div style={{ animation: 'maintSuccessIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 80, height: 80, borderRadius: '50%',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                boxShadow: '0 4px 24px rgba(34,197,94,0.35), 0 0 40px rgba(34,197,94,0.15)',
                fontSize: 36, marginBottom: 12,
              }}>
                <i className="fa fa-check" style={{ color: '#fff' }} />
              </div>
              <h1 style={{
                color: '#fff', fontSize: 28, fontWeight: 700, margin: '20px 0 10px',
                letterSpacing: '-0.01em',
              }}>
                Welcome Back
              </h1>
              <p style={{ color: '#8888a0', fontSize: 15, margin: '0 0 8px' }}>
                Redirecting to dashboard...
              </p>
            </div>
          ) : (
            <div>

          {/* Icon with pulse glow */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #c9a84c, #b8953f)',
            boxShadow: '0 4px 24px rgba(201,168,76,0.35), 0 0 40px rgba(201,168,76,0.15)',
            fontSize: 34, marginBottom: 12,
            animation: 'maintIconPulse 3s ease-in-out infinite',
          }}>
            <i className="fa fa-wrench" style={{ color: '#0a0a0f', transform: 'rotate(-15deg)' }} />
          </div>

          <h1 style={{
            color: '#fff', fontSize: 28, fontWeight: 700, margin: '20px 0 10px',
            letterSpacing: '-0.01em',
            background: 'linear-gradient(135deg, #ffffff 0%, #d4d4d4 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            Under Maintenance
          </h1>

          <div style={{ marginBottom: 28 }}>
            <span style={{
              display: 'inline-block', padding: '5px 16px',
              background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 20, color: '#c9a84c', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
            }}>
              Temporarily Unavailable
            </span>
          </div>

          <p style={{
            color: '#8888a0', fontSize: 15, lineHeight: 1.7, margin: '0 0 36px',
            maxWidth: 340, marginLeft: 'auto', marginRight: 'auto',
          }}>
            {msg}
          </p>

          <button
            type="button"
            onClick={function () { _this.checkStatus(); }}
            style={{
              padding: '14px 44px', fontSize: 15, fontWeight: 700, color: '#0a0a0f',
              background: 'linear-gradient(135deg, #c9a84c, #b8953f)', border: 'none',
              borderRadius: 12, cursor: 'pointer',
              transition: 'transform 0.15s ease, box-shadow 0.25s ease, opacity 0.2s ease',
              fontFamily: "'Lato', sans-serif", letterSpacing: '0.02em',
              boxShadow: '0 4px 16px rgba(201,168,76,0.25)',
            }}
            onMouseEnter={function (e) { e.target.style.boxShadow = '0 6px 28px rgba(201,168,76,0.4)'; e.target.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={function (e) { e.target.style.boxShadow = '0 4px 16px rgba(201,168,76,0.25)'; e.target.style.transform = 'translateY(0)'; }}
            onMouseDown={function (e) { e.target.style.transform = 'scale(0.97)'; }}
            onMouseUp={function (e) { e.target.style.transform = 'translateY(-1px)'; }}
          >
            Try Again
          </button>

          {/* Divider */}
          <div style={{
            margin: '32px auto 16px', width: 60, height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.25), transparent)',
          }} />

          {!_this.state.showLogin ? (
            <div style={{ animation: 'maintSlideIn 0.3s ease forwards' }}>
              <p style={{ color: '#444', fontSize: 12, marginBottom: 6, letterSpacing: '0.04em' }}>
                Primal Marketplace
              </p>
              <button
                type="button"
                onClick={function () { _this.openLogin(); }}
                style={{ color: '#444', fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.2s ease', letterSpacing: '0.02em', padding: 0, fontFamily: "'Lato', sans-serif" }}
                onMouseEnter={function (e) { e.target.style.color = '#c9a84c'; }}
                onMouseLeave={function (e) { e.target.style.color = '#444'; }}
              >
                Admin Login
              </button>
            </div>
          ) : _this.state.showLogin === 'exiting' ? (
            <div style={{ animation: 'maintSlideOut 0.3s ease forwards' }}>
              <p style={{ color: '#444', fontSize: 12, marginBottom: 6, letterSpacing: '0.04em' }}>
                Primal Marketplace
              </p>
              <p style={{ color: '#444', fontSize: 11, margin: 0 }}>Admin Login</p>
            </div>
          ) : (
            <form
              onSubmit={function (e) { _this.handleLogin(e); }}
              style={{ textAlign: 'left', animation: _this.state.loginView ? 'maintSlideIn 0.35s ease forwards' : 'maintSlideOut 0.3s ease forwards' }}
            >
              <p style={{ color: '#8888a0', fontSize: 13, textAlign: 'center', margin: '0 0 16px', fontWeight: 600 }}>
                <i className="fa fa-lock" style={{ marginRight: 6, color: '#c9a84c', fontSize: 12 }} />
                Admin Sign In
              </p>
              <div style={{ marginBottom: 12 }}>
                <input
                  type="email"
                  value={_this.state.loginEmail}
                  onChange={function (e) { _this.setState({ loginEmail: e.target.value, loginError: '' }); }}
                  placeholder="Email address"
                  autoFocus
                  style={{
                    width: '100%', padding: '12px 14px', fontSize: 16, color: '#fff',
                    background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.1)',
                    borderRadius: 10, outline: 'none', transition: 'border-color 0.2s',
                    boxSizing: 'border-box', fontFamily: "'Lato', sans-serif",
                  }}
                  onFocus={function (e) { if (!_this.state.loginError) e.target.style.borderColor = '#c9a84c'; }}
                  onBlur={function (e) { if (!_this.state.loginError) e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <input
                  type="password"
                  value={_this.state.loginPassword}
                  onChange={function (e) { _this.setState({ loginPassword: e.target.value, loginError: '' }); }}
                  placeholder="Password"
                  style={{
                    width: '100%', padding: '12px 14px', fontSize: 16, color: '#fff',
                    background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.1)',
                    borderRadius: 10, outline: 'none', transition: 'border-color 0.2s',
                    boxSizing: 'border-box', fontFamily: "'Lato', sans-serif",
                  }}
                  onFocus={function (e) { if (!_this.state.loginError) e.target.style.borderColor = '#c9a84c'; }}
                  onBlur={function (e) { if (!_this.state.loginError) e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>
              {_this.state.loginError && (
                <p style={{ color: '#ef4444', fontSize: 13, margin: '0 0 12px 2px' }}>
                  {_this.state.loginError}
                </p>
              )}
              <button
                type="submit"
                disabled={_this.state.loginLoading}
                style={{
                  width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 700, color: '#0a0a0f',
                  background: 'linear-gradient(135deg, #c9a84c, #b8953f)', border: 'none',
                  borderRadius: 10, cursor: _this.state.loginLoading ? 'wait' : 'pointer',
                  transition: 'opacity 0.2s, transform 0.15s',
                  fontFamily: "'Lato', sans-serif", letterSpacing: '0.02em',
                  opacity: _this.state.loginLoading ? 0.7 : 1,
                  boxShadow: '0 4px 12px rgba(201,168,76,0.2)',
                }}
                onMouseEnter={function (e) { if (!_this.state.loginLoading) { e.target.style.boxShadow = '0 6px 20px rgba(201,168,76,0.35)'; e.target.style.transform = 'translateY(-1px)'; } }}
                onMouseLeave={function (e) { e.target.style.boxShadow = '0 4px 12px rgba(201,168,76,0.2)'; e.target.style.transform = 'translateY(0)'; }}
              >
                {_this.state.loginLoading ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={function () { _this.closeLogin(); }}
                style={{ display: 'block', width: '100%', marginTop: 12, color: '#555', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Lato', sans-serif", textAlign: 'center', transition: 'color 0.2s' }}
                onMouseEnter={function (e) { e.target.style.color = '#888'; }}
                onMouseLeave={function (e) { e.target.style.color = '#555'; }}
              >
                &larr; Back
              </button>
            </form>
          )}

            </div>
          )}
        </div>

        {/* Animations */}
        <style>{'\
          @keyframes maintCardIn {\
            from { opacity: 0; transform: scale(0.95) translateY(12px); }\
            to { opacity: 1; transform: scale(1) translateY(0); }\
          }\
          @keyframes maintIconPulse {\
            0%, 100% { box-shadow: 0 4px 24px rgba(201,168,76,0.35), 0 0 40px rgba(201,168,76,0.15); }\
            50% { box-shadow: 0 4px 32px rgba(201,168,76,0.5), 0 0 60px rgba(201,168,76,0.25); }\
          }\
          @keyframes maintGlow {\
            from { opacity: 0.6; transform: translate(-50%, -50%) scale(0.9); }\
            to { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }\
          }\
          @keyframes maintSuccessIn {\
            from { opacity: 0; transform: scale(0.8); }\
            to { opacity: 1; transform: scale(1); }\
          }\
          @keyframes maintFadeOut {\
            from { opacity: 1; }\
            to { opacity: 0; }\
          }\
          @keyframes maintSlideIn {\
            from { opacity: 0; transform: translateY(10px); }\
            to { opacity: 1; transform: translateY(0); }\
          }\
          @keyframes maintSlideOut {\
            from { opacity: 1; transform: translateY(0); }\
            to { opacity: 0; transform: translateY(-10px); }\
          }\
        '}</style>
        </div>
      </div>
    );
  }
}

ReactDOM.render(
  <BetaGate>
  <MaintenanceGate>
  <ThemeProvider>
    <AuthModalProvider>
      <CartProvider>
      <GameProvider>
      <Router>
        <ScrollToTop>
        <ErrorBoundary>
        <div>
          <UpdateOnNavigate />
          <AuthModal />
          <Toast />

          <Switch>

          {/* New layout shell — homepage */}
          <Route exact path='/' render={props => (
            <Layout><Homepage {...props} /></Layout>
          )} />

          {/* Existing routes — unchanged */}
          <Route exact path='/app' render={props => <DeepDescription {...props} />} />
          <Route exact path='/more' render={props => (
            <DeepDescription {...props} />
          )} />
          <Route exact path='/sharingmore' render={props => (
            <Layout><SharingDescription {...props} /></Layout>
          )} />
          <Route exact path='/terms' render={props => (
            <Layout><TermsOfService {...props} /></Layout>
          )} />
          <Route exact path='/privacy' render={props => (
            <Layout><PrivacyPolicy {...props} /></Layout>
          )} />
          <Route exact path='/shipping' render={props => (
            <Layout><ShippingGuide {...props} /></Layout>
          )} />
          <Route exact path='/cart-optimizer' render={props => (
            <Layout><CartOptimizer {...props} /></Layout>
          )} />
          <Route exact path='/contact' render={props => (
            <Layout><ContactUs {...props} /></Layout>
          )} />
          <Route exact path='/how-to-sell' render={props => (
            <Layout><HowToSell {...props} /></Layout>
          )} />
          <Route exact path='/how-to-buy' render={props => (
            <Layout><HowToBuy {...props} /></Layout>
          )} />
          <Route exact path='/payouts' render={props => (
            <Layout><ReceivingPayouts {...props} /></Layout>
          )} />
          <Route exact path='/faq' render={props => (
            <Layout><FAQ {...props} /></Layout>
          )} />
          <Route path='/reset-password/:token' render={props => (
            <Layout><ResetPassword {...props} /></Layout>
          )} />
          <Route path='/verify-email/:token' render={props => (
            <Layout><VerifyEmail {...props} /></Layout>
          )} />
          <Route path='/share/:share_id' render={props => <ShareRedirect {...props} />} />
          <Route exact path='/browse' render={props => (
            <Layout><Browse {...props} /></Layout>
          )} />
          <Route exact path='/browse/:gameSlug' render={props => (
            <Layout><Browse {...props} /></Layout>
          )} />
          <Route path='/browse/:gameSlug/:setName' render={props => (
            <Layout><Browse {...props} /></Layout>
          )} />
          <Route exact path='/listings' render={props => (
            <Layout><AllListings {...props} /></Layout>
          )} />
          <Route path='/card/:card_id' render={props => (
            <Layout><CardDetail {...props} /></Layout>
          )} />
          <Route path='/product/:product_id' render={props => (
            <Layout><ProductViewRedesign {...props} /></Layout>
          )} />
          <Route path='/purchase/:product_id' render={props => <PurchasePost {...props} />} />
          <Route path='/metapurchase/:share_id' render={props => <PurchaseSharedPost {...props} />} />
          <Route path='/search/:query' render={props => (
            <Layout><Search {...props} /></Layout>
          )} />
          <Route exact path='/login' render={props => <AuthRedirect tab='login' {...props} />} />
          <Route exact path='/signup' render={props => <AuthRedirect tab='signup' {...props} />} />
          <Route exact path='/register' render={props => <AuthRedirect tab='signup' {...props} />} />
          <Route exact path='/sell/bulk' render={props => (
            <Layout><BulkPost {...props} /></Layout>
          )} />
          <Route exact path='/sell' render={props => (
            <Layout><Post {...props} /></Layout>
          )} />
          <Route exact path='/edit/:product_id' render={props => (
            <Layout><EditPost {...props} /></Layout>
          )} />
          <Route exact path='/cart' render={props => (
            <Layout><CartPage {...props} /></Layout>
          )} />
          <Route exact path='/cart/checkout' render={props => (
            <Layout><CartCheckout {...props} /></Layout>
          )} />
          <Route exact path='/order-confirmation' render={props => (
            <Layout><OrderConfirmation {...props} /></Layout>
          )} />
          <Route exact path='/orders' render={props => <OrdersPage {...props} />} />
          <Route exact path='/messages' render={props => <Messages {...props} />} />
          {/* BUYLIST FEATURE: disabled — preserved for future use */}
          {/* <Route exact path='/buylist' render={props => <BuylistManager {...props} />} /> */}
          {/* <Route exact path='/buylist/checkout/:offerId' render={props => (
            <Layout><BuylistCheckout {...props} /></Layout>
          )} /> */}
          <Route exact path='/analytics' render={() => <Redirect to='/admin/analytics' />} />
          <Route path='/admin' render={props => <AdminLayout {...props} />} />
          <Route exact path='/account-standing' render={props => (
            <Layout><AccountStanding {...props} /></Layout>
          )} />
          <Route exact path='/settings' render={props => <Settings {...props} />} />
          <Route path='/shop/:username' render={props => <Profile {...props} />} />
          {/* /notifications route removed - notifications now in navbar dropdown */}
          <Route path='/reviews/:user_id' render={props => <ReviewsList {...props} />} />
          <Route path='/discover/:category' render={props => <Discover {...props} />} />
          {/* Followers/Following routes removed — now shown as modal in Profile.js */}
          <Route path='/shares/:post_id' render={props => <PostSharesList {...props} />} />
          <Route path='/favorites/:post_id' render={props => <PostFavoritesList {...props} />} />

          {/* 404 catch-all — must be last */}
          <Route render={props => (
            <Layout><NotFound {...props} /></Layout>
          )} />

          </Switch>
        </div>
        </ErrorBoundary>
        </ScrollToTop>
      </Router>
      </GameProvider>
      </CartProvider>
    </AuthModalProvider>
  </ThemeProvider>
  </MaintenanceGate>
  </BetaGate>,
  document.getElementById('root')
);
unregisterServiceWorker();
